export interface DraftImage {
  url: string;
  caption: string;
  cover: boolean;
}

export type DraftBlock =
  | { id: string; kind: "paragraph" | "quote" | "callout" | "bulleted" | "numbered" | "code"; text: string }
  | { id: string; kind: "heading"; level: 1 | 2 | 3; text: string }
  | { id: string; kind: "image"; url: string; caption: string }
  | { id: string; kind: "divider" };

export interface TopicDraft {
  title: string;
  body: DraftBlock[];
  /** Blocks under a heading named 口播稿. Empty when the page has no script section. */
  script: DraftBlock[];
  notes: DraftBlock[];
  images: DraftImage[];
  tags: string[];
  fetchedAt: string;
}

export interface RawRichText {
  plain_text?: string;
}

export interface RawBlock {
  id: string;
  type: string;
  has_children?: boolean;
  children?: RawBlock[];
  paragraph?: { rich_text?: RawRichText[] };
  heading_1?: { rich_text?: RawRichText[] };
  heading_2?: { rich_text?: RawRichText[] };
  heading_3?: { rich_text?: RawRichText[] };
  bulleted_list_item?: { rich_text?: RawRichText[] };
  numbered_list_item?: { rich_text?: RawRichText[] };
  quote?: { rich_text?: RawRichText[] };
  callout?: { rich_text?: RawRichText[] };
  toggle?: { rich_text?: RawRichText[] };
  to_do?: { rich_text?: RawRichText[]; checked?: boolean };
  code?: { rich_text?: RawRichText[] };
  child_page?: { title?: string };
  bookmark?: { url?: string; caption?: RawRichText[] };
  image?: {
    caption?: RawRichText[];
    file?: { url?: string };
    external?: { url?: string };
  };
  file?: {
    name?: string;
    caption?: RawRichText[];
    file?: { url?: string };
    external?: { url?: string };
  };
}

const CONTAINERS = new Set(["column_list", "column", "synced_block"]);
const TITLE_LABELS = ["标题"];
const BODY_LABELS = ["正文", "文案", "笔记正文"];
const TAG_LABELS = ["标签", "话题"];
const NOTE_LABELS = ["方法", "备注", "笔记方法", "修改意见", "内部备注", "创作说明", "发布备注", "注意事项"];
const COVER_LABELS = ["封面"];

type SectionKind = "body" | "title" | "tags" | "notes" | "cover" | "copy" | "gallery" | "script";

export function revisionLine(comment: string, today: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(today);
  const label = match ? `${Number(match[2])}月${Number(match[3])}日` : today;
  return `Olando 修改意见（${label}）：${comment.trim()}`;
}

export function revisionCalloutBlock(line: string): Record<string, unknown> {
  return {
    object: "block",
    type: "callout",
    callout: {
      rich_text: [{ type: "text", text: { content: line.slice(0, 2000) } }],
      icon: { type: "emoji", emoji: "💬" },
      color: "yellow_background",
    },
  };
}

export function draftBodyText(draft: Pick<TopicDraft, "body">): string {
  return draft.body
    .flatMap((block) => (block.kind === "image" || block.kind === "divider" ? [] : [block.text.trim()]))
    .filter(Boolean)
    .join("\n");
}

export function draftCoverUrl(draft: Pick<TopicDraft, "images">): string | null {
  const images = draft.images.filter((image) => image.url.startsWith("https://"));
  const cover = images.find((image) => image.cover) ?? images[0];
  return cover?.url ?? null;
}

/** Notion blocks for an edited note. The code block is what the draft parser reads back. */
export function publishCopyBlocks(input: { title: string; body: string; tags: readonly string[] }): unknown[] {
  const tags = input.tags.map((tag) => tag.trim()).filter(Boolean).map((tag) => (tag.startsWith("#") ? tag : `#${tag}`));
  const lines = [`标题：${input.title.trim() || "未命名选题"}`, `正文：${input.body.trim()}`];
  if (tags.length) lines.push(tags.join(" "));
  return [
    {
      object: "block",
      type: "heading_2",
      heading_2: { rich_text: [{ type: "text", text: { content: "发布文案" } }] },
    },
    {
      object: "block",
      type: "code",
      code: { rich_text: [{ type: "text", text: { content: lines.join("\n").slice(0, 1900) } }], language: "plain text" },
    },
  ];
}

export function isRevisionNote(text: string): boolean {
  const trimmed = text.trim();
  return /^Olando\s*修改意见/.test(trimmed) || /^修改意见\s*[:：]/.test(trimmed);
}

/** Page id that should receive a 退回修改 callout. Prefer the 成品 child; otherwise the database row. */
/** Image blocks on the database page that sit outside the 成品 child pages. */
export function looseImageBlocks(blocks: readonly RawBlock[], skipPageIds: ReadonlySet<string>): RawBlock[] {
  const found: RawBlock[] = [];
  const walk = (list: readonly RawBlock[]) => {
    for (const block of list) {
      if (block.type === "child_page" && block.id && skipPageIds.has(block.id)) continue;
      if (block.type === "image" || block.type === "file") found.push(block);
      if (block.children?.length) walk(block.children);
    }
  };
  walk(blocks);
  return found;
}

export function finishedDraftPageIds(
  blocks: ReadonlyArray<Pick<RawBlock, "id" | "type" | "child_page">>,
): string[] {
  return blocks
    .filter((block) => block.type === "child_page" && (block.child_page?.title ?? "").trim().startsWith("成品") && block.id)
    .map((block) => block.id);
}

export function parseTopicDraft(input: {
  pageTitle: string;
  blocks: RawBlock[];
  propertyTags?: string[];
  revisionNote?: string;
  fetchedAt: string;
}): TopicDraft {
  const body: DraftBlock[] = [];
  const script: DraftBlock[] = [];
  const notes: DraftBlock[] = [];
  const images: DraftImage[] = [];
  const tags = new Set((input.propertyTags ?? []).map((tag) => tag.trim()).filter(Boolean));
  let title = "";
  let mode: SectionKind = "body";

  const absorbText = (section: SectionKind, text: string, block: DraftBlock) => {
    if (block.kind === "image") {
      if (block.url.trim()) images.push({ url: block.url, caption: block.caption, cover: false });
      return;
    }
    if (!text.trim() && block.kind !== "divider") return;
    if (section === "title") {
      const next = text.trim();
      title = title && next ? `${title} ${next}` : title || next;
      return;
    }
    if (section === "tags") {
      for (const tag of splitTags(text)) tags.add(tag);
      return;
    }
    if (section === "notes" || section === "gallery") notes.push(block);
    else if (section === "script") script.push(block);
    else body.push(block);
  };

  const applyPublishCopy = (id: string, text: string) => {
    let copying = false;
    text.split("\n").forEach((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const titleMatch = /^标题\s*[:：]\s*(.*)$/.exec(trimmed);
      if (titleMatch) {
        const next = (titleMatch[1] ?? "").trim();
        if (next) title = next;
        copying = false;
        return;
      }
      const bodyMatch = /^正文\s*[:：]\s*(.*)$/.exec(trimmed);
      if (bodyMatch) {
        copying = true;
        const rest = (bodyMatch[1] ?? "").trim();
        if (rest) body.push({ id: `${id}-line-${index}`, kind: "paragraph", text: rest });
        return;
      }
      if (/^(?:[#＃][^\s#＃]+)(?:\s+[#＃][^\s#＃]+)*$/.test(trimmed)) {
        for (const tag of splitTags(trimmed)) tags.add(tag);
        return;
      }
      if (copying || mode === "copy") body.push({ id: `${id}-line-${index}`, kind: "paragraph", text: trimmed });
    });
  };

  const consume = (blocks: RawBlock[]) => {
    for (const block of blocks) {
      if (CONTAINERS.has(block.type)) {
        consume(block.children ?? []);
        continue;
      }
      const text = blockText(block);
      const heading = block.type.startsWith("heading_");
      if (block.type === "code" && (mode === "copy" || (/标题\s*[:：]/.test(text) && /正文\s*[:：]/.test(text)))) {
        applyPublishCopy(block.id, text);
        continue;
      }
      if ((block.type === "quote" || block.type === "callout") && /状态\s*[:：]/.test(text) && /(待审阅|待发布|计划)/.test(text)) {
        const rendered = renderBlock(block);
        if (rendered) notes.push(rendered);
        continue;
      }
      const section =
        block.type === "bulleted_list_item" || block.type === "numbered_list_item" || block.type === "to_do"
          ? null
          : matchSection(text, heading);
      if (section) {
        mode = section.kind;
        if (section.kind === "notes" && heading) {
          const rendered = renderBlock(block);
          if (rendered) notes.push(rendered);
        } else if (section.rest) {
          const synthetic = textBlock(block.id, "paragraph", section.rest);
          absorbText(section.kind, section.rest, synthetic);
        }
        if (block.children?.length) consume(block.children);
        continue;
      }
      if (isRevisionNote(text)) {
        const rendered = renderBlock(block);
        if (rendered && rendered.kind !== "divider") notes.push(rendered);
        if (block.children?.length) {
          const previous = mode;
          mode = "notes";
          consume(block.children);
          mode = previous;
        }
        continue;
      }
      const rendered = renderBlock(block);
      if (rendered) absorbText(mode === "cover" ? "body" : mode, text, rendered);
      if (block.children?.length) consume(block.children);
    }
  };

  consume(input.blocks);
  if (input.revisionNote?.trim()) {
    notes.unshift({ id: "revision-property", kind: "callout", text: input.revisionNote.trim() });
  }
  const ordered = orderImages(images);
  return {
    title: title || input.pageTitle || "未命名选题",
    body,
    script,
    notes,
    images: ordered,
    tags: [...tags],
    fetchedAt: input.fetchedAt,
  };
}

function orderImages(images: DraftImage[]): DraftImage[] {
  if (!images.length) return [];
  const found = images.findIndex((image) => image.caption.includes("封面"));
  const coverIndex = found >= 0 ? found : 0;
  const cover = images[coverIndex];
  if (!cover) return [];
  return [
    { ...cover, cover: true },
    ...images.filter((_, index) => index !== coverIndex).map((image) => ({ ...image, cover: false })),
  ];
}

function matchSection(text: string, heading: boolean): { kind: SectionKind; rest: string } | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const groups: Array<[SectionKind, string[]]> = [
    ["title", TITLE_LABELS],
    ["body", BODY_LABELS],
    ["tags", TAG_LABELS],
    ["notes", NOTE_LABELS],
    ["cover", COVER_LABELS],
  ];
  for (const [kind, labels] of groups) {
    for (const label of labels) {
      if (heading && /^发布文案/.test(trimmed)) return { kind: "copy", rest: "" };
      if (heading && /^口播稿\s*[:：]?$/.test(trimmed)) return { kind: "script", rest: "" };
      if (heading && /^图片总览/.test(trimmed)) return { kind: "gallery", rest: "" };
      if (heading && /^(实测记录|方法|备注|笔记方法|修改意见|内部备注|创作说明|发布备注|注意事项)/.test(trimmed)) {
        return { kind: "notes", rest: "" };
      }
      if (heading && (trimmed === label || trimmed === `${label}：` || trimmed === `${label}:`)) {
        return { kind, rest: "" };
      }
      const match = new RegExp(`^${label}\\s*[:：]\\s*([\\s\\S]*)$`).exec(trimmed);
      if (match) return { kind, rest: (match[1] ?? "").trim() };
    }
  }
  return null;
}

function renderBlock(block: RawBlock): DraftBlock | null {
  const text = blockText(block);
  if (block.type === "divider") return { id: block.id, kind: "divider" };
  if (block.type === "image") {
    const url = block.image?.file?.url || block.image?.external?.url || "";
    if (!url) return null;
    return { id: block.id, kind: "image", url, caption: plain(block.image?.caption) };
  }
  if (block.type === "file") {
    const url = block.file?.file?.url || block.file?.external?.url || "";
    const name = block.file?.name || url;
    if (url && /\.(png|jpe?g|webp|gif|heic)(\?|$)/i.test(name)) {
      return { id: block.id, kind: "image", url, caption: plain(block.file?.caption) };
    }
  }
  if (!text) return null;
  if (block.type === "heading_1") return { id: block.id, kind: "heading", level: 1, text };
  if (block.type === "heading_2") return { id: block.id, kind: "heading", level: 2, text };
  if (block.type === "heading_3") return { id: block.id, kind: "heading", level: 3, text };
  if (block.type === "bulleted_list_item") return { id: block.id, kind: "bulleted", text };
  if (block.type === "numbered_list_item") return { id: block.id, kind: "numbered", text };
  if (block.type === "quote") return { id: block.id, kind: "quote", text };
  if (block.type === "callout") return { id: block.id, kind: "callout", text };
  if (block.type === "code") return { id: block.id, kind: "code", text };
  if (block.type === "to_do") return { id: block.id, kind: "bulleted", text: `${block.to_do?.checked ? "[x]" : "[ ]"} ${text}` };
  if (block.type === "bookmark") return { id: block.id, kind: "paragraph", text: block.bookmark?.url || text };
  return { id: block.id, kind: "paragraph", text };
}

function textBlock(id: string, kind: "paragraph", text: string): DraftBlock {
  return { id: `${id}-rest`, kind, text };
}

function blockText(block: RawBlock): string {
  if (block.type === "bookmark") return plain(block.bookmark?.caption) || block.bookmark?.url || "";
  if (block.type === "image") return plain(block.image?.caption);
  const payload = (block as unknown as Record<string, { rich_text?: RawRichText[] } | undefined>)[block.type];
  return plain(payload?.rich_text);
}

function plain(items: RawRichText[] | undefined): string {
  return (items ?? []).map((item) => item.plain_text ?? "").join("").trim();
}

function splitTags(text: string): string[] {
  return text
    .split(/[#＃,，、\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}
